// pages/SuperAdminPage.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Tooltip,
  Switch,
  IconButton,
  useMediaQuery,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import TuneIcon from '@mui/icons-material/Tune';
import RefreshIcon from '@mui/icons-material/Refresh';
import ConstructionIcon from '@mui/icons-material/Construction';
import {
  fetchPlatformStats,
  fetchAccounts,
  fetchAllUsers,
  fetchPlatformSettings,
  updatePlatformSettings,
  clearSuperAdminError,
} from '../slices/superAdminSlice';
import { logoutUser } from '../slices/authSlice';
import { useToast } from '../context/ToastContext';
import SuperAdminOverviewTab from '../components/superAdmin/SuperAdminOverviewTab';
import SuperAdminAccountsTab from '../components/superAdmin/SuperAdminAccountsTab';
import SuperAdminUsersTab from '../components/superAdmin/SuperAdminUsersTab';
import SuperAdminSettingsTab from '../components/superAdmin/SuperAdminSettingsTab';
import { SA } from '../components/superAdmin/theme';

const SECTIONS = [
  { key: 'overview', label: 'نظرة عامة', icon: <DashboardOutlinedIcon /> },
  { key: 'accounts', label: 'المدارس', icon: <SchoolOutlinedIcon /> },
  { key: 'users', label: 'المستخدمون', icon: <PeopleOutlineIcon /> },
  { key: 'settings', label: 'إعدادات المنصة', icon: <TuneIcon /> },
];

const SuperAdminPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { stats, accounts, users, settings, error } = useSelector((state) => state.superAdmin);
  const [section, setSection] = useState('overview');
  // Lets the overview jump into the schools list pre-filtered / with a school open
  const [accountsIntent, setAccountsIntent] = useState(null);
  const compact = useMediaQuery('(max-width:900px)');

  const refreshAll = () => {
    dispatch(fetchPlatformStats());
    dispatch(fetchAccounts());
    dispatch(fetchAllUsers());
    dispatch(fetchPlatformSettings());
  };

  useEffect(() => {
    document.title = 'لوحة تحكم المنصة';
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (error) {
      showToast(error.detail || error.error || 'حدث خطأ', 'error');
      dispatch(clearSuperAdminError());
    }
  }, [error, showToast, dispatch]);

  const handleLogout = () => {
    dispatch(logoutUser());
    navigate('/');
  };

  const toggleMaintenance = async () => {
    const next = !settings?.maintenance_mode;
    try {
      await dispatch(updatePlatformSettings({ maintenance_mode: next })).unwrap();
      showToast(next ? 'تم تفعيل وضع الصيانة — تم منع دخول المستخدمين' : 'تم إيقاف وضع الصيانة', next ? 'warning' : 'success');
    } catch {
      // error toast handled by the slice error effect
    }
  };

  const goToAccounts = (intent) => {
    setAccountsIntent({ ...intent, at: Date.now() });
    setSection('accounts');
  };

  const current = SECTIONS.find((s) => s.key === section);

  return (
    <Box sx={{ direction: 'rtl', fontFamily: SA.font, display: 'flex', minHeight: '100vh', bgcolor: SA.bg }}>
      {/* Sidebar */}
      <Box
        component="nav"
        sx={{
          width: compact ? 72 : 240,
          flexShrink: 0,
          bgcolor: SA.ink,
          color: '#fff',
          display: 'flex',
          flexDirection: 'column',
          position: 'sticky',
          top: 0,
          height: '100vh',
          transition: 'width .2s',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: compact ? 0 : 2.5, py: 2.5, justifyContent: compact ? 'center' : 'flex-start' }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: SA.primary, display: 'grid', placeItems: 'center' }}>
            <AdminPanelSettingsIcon fontSize="small" />
          </Box>
          {!compact && (
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 15, lineHeight: 1.2, fontFamily: SA.font }}>إدارة المنصة</Typography>
              <Typography sx={{ fontSize: 11, opacity: 0.55, fontFamily: SA.font }}>Super Admin</Typography>
            </Box>
          )}
        </Box>

        <Box sx={{ px: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5, flexGrow: 1 }}>
          {SECTIONS.map((s) => {
            const active = s.key === section;
            return (
              <Tooltip key={s.key} title={compact ? s.label : ''} placement="left">
                <Box
                  onClick={() => setSection(s.key)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 1.5,
                    py: 1.1,
                    borderRadius: 2,
                    cursor: 'pointer',
                    justifyContent: compact ? 'center' : 'flex-start',
                    bgcolor: active ? 'rgba(255,255,255,0.10)' : 'transparent',
                    color: active ? '#fff' : 'rgba(255,255,255,0.62)',
                    '&:hover': { bgcolor: 'rgba(255,255,255,0.06)', color: '#fff' },
                    '& svg': { fontSize: 20, color: active ? SA.primaryLight : 'inherit' },
                  }}
                >
                  {s.icon}
                  {!compact && <Typography sx={{ fontSize: 14, fontWeight: active ? 700 : 500, fontFamily: SA.font }}>{s.label}</Typography>}
                </Box>
              </Tooltip>
            );
          })}
        </Box>

        <Box sx={{ p: 1.5 }}>
          <Button
            fullWidth
            onClick={handleLogout}
            startIcon={compact ? null : <LogoutIcon />}
            sx={{ color: 'rgba(255,255,255,0.7)', justifyContent: compact ? 'center' : 'flex-start', fontFamily: SA.font, '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.06)' } }}
          >
            {compact ? <LogoutIcon fontSize="small" /> : 'تسجيل الخروج'}
          </Button>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        {settings?.maintenance_mode && (
          <Box sx={{ bgcolor: SA.warnBg, color: SA.warnInk, px: 3, py: 1, display: 'flex', alignItems: 'center', gap: 1, fontSize: 14, borderBottom: `1px solid ${SA.warnBorder}` }}>
            <ConstructionIcon fontSize="small" />
            <Typography sx={{ fontSize: 14, fontWeight: 600, fontFamily: SA.font }}>
              وضع الصيانة مفعّل — لا يستطيع مستخدمو المدارس تسجيل الدخول الآن.
            </Typography>
          </Box>
        )}

        <Box sx={{ px: { xs: 2, md: 4 }, py: 3, maxWidth: 1400, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: 24, fontWeight: 800, color: SA.text, fontFamily: SA.font }}>{current.label}</Typography>
            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1.5, py: 0.25, borderRadius: 99, border: `1px solid ${SA.border}`, bgcolor: '#fff' }}>
              <ConstructionIcon sx={{ fontSize: 18, color: settings?.maintenance_mode ? SA.warnInk : SA.muted }} />
              <Typography sx={{ fontSize: 13, color: SA.muted, fontFamily: SA.font }}>وضع الصيانة</Typography>
              <Switch size="small" color="warning" checked={!!settings?.maintenance_mode} onChange={toggleMaintenance} disabled={!settings} />
            </Box>
            {settings?.announcement_enabled && (
              <Chip label="إعلان منشور" size="small" color="info" variant="outlined" onClick={() => setSection('settings')} />
            )}
            <Tooltip title="تحديث البيانات">
              <IconButton onClick={refreshAll} sx={{ border: `1px solid ${SA.border}`, bgcolor: '#fff' }}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          {section === 'overview' && (
            <SuperAdminOverviewTab stats={stats} accounts={accounts} settings={settings} onOpenAccounts={goToAccounts} />
          )}
          {section === 'accounts' && (
            <SuperAdminAccountsTab accounts={accounts} settings={settings} intent={accountsIntent} onRefresh={refreshAll} />
          )}
          {section === 'users' && (
            <SuperAdminUsersTab users={users} accounts={accounts} onRefresh={refreshAll} />
          )}
          {section === 'settings' && <SuperAdminSettingsTab settings={settings} />}
        </Box>
      </Box>
    </Box>
  );
};

export default SuperAdminPage;
